(() => {
  if (window.monclerOverlaysInitialized) return;
  window.monclerOverlaysInitialized = true;
  const openers = new WeakMap();
  // Keyboard vs touch/mouse, for the focus styles in moncler-product-overlays.css.
  document.addEventListener('keydown', event => { if (event.key === 'Tab') document.documentElement.classList.add('moncler-kbd'); });
  document.addEventListener('pointerdown', () => document.documentElement.classList.remove('moncler-kbd'), {passive: true});
  const syncLock = () => document.documentElement.classList.toggle('moncler-overlay-open', !!document.querySelector('.moncler-overlay-dialog[open]'));
  // Slide out first ([data-closing] in moncler-product-overlays.css), then close.
  const closeDialog = dialog => {
    if (!dialog?.open || dialog.hasAttribute('data-closing')) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { dialog.close(); return; }
    dialog.setAttribute('data-closing', '');
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      dialog.removeEventListener('transitionend', onEnd);
      dialog.removeAttribute('data-closing');
      dialog.close();
    };
    const onEnd = event => { if (event.target === dialog && event.propertyName === 'transform') finish(); };
    dialog.addEventListener('transitionend', onEnd);
    setTimeout(finish, 360);
  };
  const initialize = () => document.querySelectorAll('.moncler-overlay-dialog').forEach(dialog => {
    if (dialog.dataset.initialized) return;
    dialog.dataset.initialized = 'true';
    dialog.addEventListener('close', () => {
      dialog.querySelector('video')?.pause();
      syncLock();
      const opener = openers.get(dialog);
      if (opener?.isConnected) opener.focus({preventScroll: true});
    });
    dialog.addEventListener('click', event => {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) closeDialog(dialog);
    });
    dialog.addEventListener('cancel', event => { event.preventDefault(); closeDialog(dialog); });
  });
  document.addEventListener('click', event => {
    const opener = event.target.closest('[data-moncler-dialog-open]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.monclerDialogOpen);
      if (!dialog) return;
      initialize();
      openers.set(dialog, opener);
      dialog.showModal();
      syncLock();
      dialog.querySelector('[data-moncler-dialog-close]')?.focus({preventScroll: true});
      // 3D garment (snippets/garment-3d.liquid): its CSS, Three.js and textures load only now, on first open
      const garment = dialog.querySelector('[data-garment3d]:not([data-requested])');
      if (garment) {
        garment.setAttribute('data-requested', '');
        const go = () => import(garment.dataset.js).catch(err => console.warn('[garment-3d]', err));
        if (document.querySelector('link[data-jersey-hanger-css]')) go();
        else {
          const link = Object.assign(document.createElement('link'), { rel: 'stylesheet', href: garment.dataset.css });
          link.setAttribute('data-jersey-hanger-css', '');
          link.onload = link.onerror = go;
          document.head.appendChild(link);
        }
      }
      const video = dialog.querySelector('video');
      if (video) {
        if (!video.getAttribute('src')) { video.src = video.dataset.rotationSrc; video.load(); }
        if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) video.play().catch(() => {});
      }
    }
    closeDialog(event.target.closest('[data-moncler-dialog-close]')?.closest('dialog'));
  });
  const placeGiftAbovePurchaseBar = () => {
    const bar = document.querySelector('.moncler-pdp__bottom-bar');
    if (!bar) return;
    const clearance = Math.max(24, window.innerHeight - bar.getBoundingClientRect().top + 12);
    document.documentElement.style.setProperty('--moncler-purchase-clearance', `${Math.ceil(clearance)}px`);
    // Mobile photo stage: CSS sizes it to 100svh from the gallery top (see moncler-product-overlays.css); this only
    // keeps that top current (e.g. after the announcement bar is closed). Page offset, not viewport offset.
    const gallery = document.querySelector('.moncler-pdp__gallery');
    if (gallery) {
      const galleryTop = Math.round(gallery.getBoundingClientRect().top + window.scrollY);
      document.documentElement.style.setProperty('--moncler-stage-top', `${galleryTop}px`);
      if (window.matchMedia('(max-width: 749px)').matches) document.documentElement.classList.add('moncler-photo-fit');
    }
  };
  const bar = document.querySelector('.moncler-pdp__bottom-bar');
  if (bar && window.ResizeObserver) {
    const ro = new ResizeObserver(placeGiftAbovePurchaseBar);
    ro.observe(bar);
    // Closing the announcement bar (or the header changing height) moves the gallery: re-measure the photo.
    document.querySelectorAll('.shopify-section-group-header-group, #shopify-section-header, .section-header').forEach((el) => ro.observe(el));
  }
  // The announcement bar's close button removes it from the DOM, which ResizeObserver doesn't report:
  // re-measure for ~0.6s after any tap so the photo re-fits.
  document.addEventListener('click', () => {
    [50, 150, 300, 600].forEach((ms) => setTimeout(placeGiftAbovePurchaseBar, ms));
  }, {passive: true});
  // Mobile: the wishlist heart sits on the photo (top-left) instead of in the purchase bar, so Add to cart gets
  // the full row. wishlist.js uses delegated clicks, so moving the node keeps it working.
  const heart = document.querySelector('.wishlist-heart--bar');
  const stage = document.getElementById('MonclerPDP');
  if (heart && stage) {
    const home = heart.parentElement, homeNext = heart.nextSibling;
    const mobile = window.matchMedia('(max-width: 749px)');
    const placeHeart = () => {
      if (mobile.matches) {
        if (heart.parentElement !== stage) stage.appendChild(heart);
        heart.classList.add('wishlist-heart--photo');
      } else if (heart.parentElement !== home) {
        home.insertBefore(heart, homeNext);
        heart.classList.remove('wishlist-heart--photo');
      }
    };
    placeHeart();
    mobile.addEventListener('change', placeHeart);
  }
  // "#full" photos: find the first and last rows that differ from the studio backdrop (sampled on a 160px copy) and
  // pass them to the CSS as --fit-top / --fit-bottom, which scales the photo so the person fills the height above the
  // purchase bar with a small margin (moncler-product-overlays.css).
  const fitFullPhotos = () => document.querySelectorAll('.moncler-pdp__media--full:not([data-fit])').forEach((media) => {
    const img = media.querySelector('img');
    if (!img) return;
    media.dataset.fit = 'pending';
    const url = new URL(img.getAttribute('src'), location.href);
    url.searchParams.set('width', '160');
    const probe = new Image();
    probe.crossOrigin = 'anonymous';
    probe.onload = () => {
      try {
        const w = probe.naturalWidth, h = probe.naturalHeight;
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d', {willReadFrequently: true});
        ctx.drawImage(probe, 0, 0);
        const px = ctx.getImageData(0, 0, w, h).data;
        const bg = [px[0], px[1], px[2]];
        const rowHasSubject = (y) => {
          for (let x = 0; x < w; x++) {
            const i = (y * w + x) * 4;
            if (Math.abs(px[i] - bg[0]) + Math.abs(px[i + 1] - bg[1]) + Math.abs(px[i + 2] - bg[2]) > 60) return true;
          }
          return false;
        };
        let top = 0, bottom = h - 1, left = w, right = -1;
        while (top < h && !rowHasSubject(top)) top++;
        while (bottom > top && !rowHasSubject(bottom)) bottom--;
        for (let y = top; y <= bottom; y++) for (let x = 0; x < w; x++) {
          const i = (y * w + x) * 4;
          if (Math.abs(px[i] - bg[0]) + Math.abs(px[i + 1] - bg[1]) + Math.abs(px[i + 2] - bg[2]) > 60) { if (x < left) left = x; if (x > right) right = x; }
        }
        const t = top / h, b = (bottom + 1) / h;
        if (b - t > 0.3 && b - t < 0.99 && right > left) {
          media.style.setProperty('--fit-top', t.toFixed(4));
          media.style.setProperty('--fit-bottom', b.toFixed(4));
          // Person width in units of the rendered photo height, so CSS can cap the zoom to keep arms in frame.
          media.style.setProperty('--fit-width', (((right + 1 - left) / w) * (w / h)).toFixed(4));
          media.classList.add('is-fitted');
        }
        media.dataset.fit = 'done';
      } catch (e) { media.dataset.fit = 'failed'; }
    };
    probe.onerror = () => { media.dataset.fit = 'failed'; };
    probe.src = url.href;
  });
  fitFullPhotos();
  window.addEventListener('resize', placeGiftAbovePurchaseBar, {passive: true});
  window.addEventListener('load', placeGiftAbovePurchaseBar, {once: true});
  placeGiftAbovePurchaseBar();
  initialize();
  document.addEventListener('shopify:section:load', () => { initialize(); placeGiftAbovePurchaseBar(); });
  document.addEventListener('shopify:section:unload', () => requestAnimationFrame(syncLock));
})();
